import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { defaultAvailability } from 'us-react-components/dist/GeneralAvailability/constants';
import { GeneralAvailability } from 'us-react-components/dist/GeneralAvailability/GeneralAvailability';
import { ProgressIndicator } from 'us-react-components/dist/ProgressIndicator/ProgressIndicator';
import { namespaces, useSharedTranslation } from 'us-react-components/dist/hooks/useSharedTranslation';
import { GeneralAvailabilityDay } from 'us-react-components/dist/types';
import { DataResponse, JobAvailabilityType, SitterGeneralAvailability, SitterGeneralAvailabilityService } from 'us-web-services';
import { Button, ButtonType } from '../../../components';
import { useDependencies } from '../../../dependencies';
import { JobAvailabilityItem } from './JobAvailabilityItem';
import { Routes } from '../../../Routes';

export const Availability = observer((): JSX.Element | null => {
  const { stores } = useDependencies();
  const navigate = useNavigate();
  const { t } = useSharedTranslation(namespaces.caregiver);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!stores.user.user?.sitter) {
    window.location.href = `${Routes.app}${Routes.parent.dashboard}`;

    return null;
  }

  const [availability, setAvailability] = useState<GeneralAvailabilityDay[]>(defaultAvailability);

  const generalAvailabilityHasSelection = (): boolean =>
    !!availability.find(x => x.timeEarly || x.timeMorning || x.timeAfternoon || x.timeEvening || x.timeOvernight);

  const saveGeneralAvailability = async (): Promise<DataResponse<SitterGeneralAvailability>> => {
    const data = {
      sitterId: stores.sitter?.sitter?.id,
      availability,
    };

    return SitterGeneralAvailabilityService.create(data);
  };

  const handleNextClick = async (): Promise<void> => {
    try {
      setIsSubmitting(true);
      try {
        await stores.sitter.availabilitySave();
        await saveGeneralAvailability();
      } catch {
        toast.error(t('notifications.genericError'));
        return;
      }

      navigate(Routes.sitter.registration.worksWith);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isNextEnabled = (): boolean => stores.sitter.availabilityHasSelection() && generalAvailabilityHasSelection();

  return (
    <div className='relative'>
      <div className='hidden lg:block absolute top-0 bottom-0 left-1/2 ml-12 right-0'>
        <div className='h-full select-none bg-nux-calendar bg-cover bg-[left_calc(57%)_top_calc(0%)]'>&nbsp;</div>
      </div>
      <div className='container'>
        <article className='overflow-auto w-auto lg:w-1/2 py-6 max-w-lg'>
          <h3 className='text-xl mb-0'>{t('pages.sitter.registration.availability.whatJobs')}</h3>
          <p className='text-sm mb-4'>{t('pages.sitter.registration.availability.deselectAny')}</p>
          <div className='lg:space-x-3'>
            <JobAvailabilityItem
              type={JobAvailabilityType.occasional}
              text={t('pages.sitter.registration.availability.occasional')}
            />
            <JobAvailabilityItem
              type={JobAvailabilityType.partTime}
              text={t('pages.sitter.registration.availability.partTime')}
              subtext={t('pages.sitter.registration.availability.partTimeHours')}
            />
            <JobAvailabilityItem
              type={JobAvailabilityType.fullTime}
              text={t('pages.sitter.registration.availability.fullTime')}
              subtext={t('pages.sitter.registration.availability.fullTimeHours')}
            />
          </div>

          <div className='max-w-[390px]'>
            <h3 className='text-xl mb-0 mt-8'>{t('pages.sitter.registration.availability.generallyAvailable')}</h3>
            <p className='text-sm mb-4'>{t('pages.sitter.registration.availability.details')}</p>

            <GeneralAvailability availability={availability} setAvailability={setAvailability} />
          </div>

          <div className='text-center float-none lg:float-right mt-8'>
            <Button
              buttonType={ButtonType.primary}
              onClick={handleNextClick}
              disabled={!isNextEnabled() || isSubmitting}
              className='mb-4'
            >
              {t('pages.sitter.registration.next')}
            </Button>
            <ProgressIndicator selected={4} total={8} />
          </div>
        </article>
      </div>
    </div>
  );
});
